import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { scrollToId, EASE } from "./Reveal";
import { useAuth, homeForRole } from "../auth/AuthContext";

const LINKS = [
  { label: "Home", id: "home" },
  { label: "Solutions", id: "solutions" },
  { label: "Products", id: "products" },
  { label: "Managed Supply", id: "managed-supply" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Supplier Network", id: "supplier-network" },
  { label: "About", id: "about" },
  { label: "Contact", id: "contact" },
];

export function Logo({ compact = false }) {
  return (
    <button
      data-testid="nav-logo"
      onClick={() => scrollToId("home")}
      className="flex items-center gap-3 group"
      aria-label="Ajemi Oil & Gas — home"
    >
      <span className="bg-white rounded-md p-1 shadow-[0_0_24px_rgba(217,164,65,0.25)] group-hover:shadow-[0_0_32px_rgba(217,164,65,0.45)] transition-shadow duration-500">
        <img src="/assets/emblem.png" alt="Ajemi Oil & Gas emblem" className="h-8 w-auto" />
      </span>
      {!compact && (
        <span className="leading-none text-left">
          <span className="block font-outfit font-semibold tracking-[0.18em] text-white text-sm">AJEMI ENERGY</span>
          <span className="block font-outfit text-[7px] sm:text-[8px] tracking-[0.22em] text-gold mt-1 whitespace-nowrap">ENERGY PROCUREMENT &amp; SUPPLY</span>
        </span>
      )}
    </button>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const authTarget = user ? homeForRole(user.role) : "/login";
  const authLabel = user ? "Portal" : "Sign In";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-black/60 backdrop-blur-xl border-b border-white/10" : "bg-transparent border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
            {LINKS.slice(1).map((l) => (
              <button
                key={l.id}
                data-testid={`nav-link-${l.id}`}
                onClick={() => go(l.id)}
                className="text-[13px] tracking-wide text-white/60 hover:text-white transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              data-testid="nav-signin-btn"
              onClick={() => navigate(authTarget)}
              className="inline-flex text-[13px] tracking-wide text-white/60 hover:text-gold transition-colors duration-300 px-2"
            >
              {authLabel}
            </button>
            <button
              data-testid="nav-request-quote-btn"
              onClick={() => go("quote")}
              className="hidden sm:inline-flex items-center gap-2 bg-gold text-ink font-outfit font-medium text-sm tracking-wide rounded-full px-5 py-2.5 hover:bg-goldbright hover:shadow-[0_0_32px_rgba(217,164,65,0.45)] transition-all duration-300"
            >
              Request a Quote
              <ArrowUpRight className="w-4 h-4" />
            </button>
            <button
              data-testid="nav-menu-btn"
              onClick={() => setOpen(true)}
              className="lg:hidden text-white/80 hover:text-white p-2"
              aria-label="Open menu"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="fixed inset-0 z-[60] bg-ink/95 backdrop-blur-2xl flex flex-col"
          >
            <div className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between w-full">
              <Logo />
              <button
                data-testid="mobile-menu-close-btn"
                onClick={() => setOpen(false)}
                className="text-white/80 hover:text-white p-2"
                aria-label="Close menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  data-testid={`mobile-nav-link-${l.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.5, ease: EASE }}
                  onClick={() => go(l.id)}
                  className="text-left font-outfit text-3xl font-light text-white/80 hover:text-gold py-2 transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="mobile-request-quote-btn"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5, ease: EASE }}
                onClick={() => go("quote")}
                className="mt-8 inline-flex w-max items-center gap-2 bg-gold text-ink font-outfit font-medium rounded-full px-7 py-3.5"
              >
                Request a Quote
                <ArrowUpRight className="w-4 h-4" />
              </motion.button>
            </div>
            <div className="px-8 pb-10 flex flex-col gap-4">
              <button
                data-testid="mobile-signin-btn"
                onClick={() => {
                  setOpen(false);
                  navigate(authTarget);
                }}
                className="inline-flex w-max items-center gap-2 border border-white/20 text-white/80 font-outfit rounded-full px-7 py-3 text-base hover:border-gold/60 hover:text-gold transition-colors"
              >
                {user ? "Go to Your Portal" : "Sign In to Portal"}
              </button>
              <a href="tel:+2349151981008" className="text-white/40 text-sm hover:text-gold transition-colors" data-testid="mobile-nav-phone">
                +234 915 198 1008
              </a>
              <a href="tel:+2348060752250" className="text-white/40 text-sm hover:text-gold transition-colors" data-testid="mobile-nav-phone-2">
                +234 806 075 2250
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
